import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Button, Card, Empty, Space, Table, Tag } from 'antd'
import { ReloadOutlined } from '@ant-design/icons'
import PageLoader from '../../components/Layouts/PageLoader'
import PageTitle from '../../components/PageTitle'
import {
  errorNotification,
  getRequest,
  trackingBillOfLadingUrl,
  trackingLinesUrl
} from '../../config'
import BillOfLadingModal from './BillOfLadingModal'
import ContactButtons from './ContactButtons'
import { errorSentence, formatAge, formatDateTime } from './utils'

// The bills of lading waiting for a person (spec: whatsapp-shipment-tracking Req 1.2).
//
// This is a queue of CONVERSATIONS, not of shipments, and it has to be: a conversation reaches
// the team because the reading could not find a shipping line, a number or a port — which is
// exactly what creating a shipment needs. Finishing one here is what brings a shipment into
// existence.
//
// A customer is sitting in a WhatsApp chat having been told a person will look, so the age of
// each row is the first thing the page says.
const BillOfLadingPage = () => {
  const [loading, setLoading] = useState(false)
  const [rows, setRows] = useState([])
  const [lines, setLines] = useState([])
  const [working, setWorking] = useState(null)
  const [loadedAt, setLoadedAt] = useState(null)

  const load = async () => {
    const response = await getRequest(
      trackingBillOfLadingUrl,
      'bills of lading',
      setLoading
    )
    if (response.success) {
      setRows(response.data.results || response.data || [])
      setLoadedAt(new Date().toISOString())
    } else {
      errorNotification(errorSentence(response, 'The queue could not be loaded.'))
    }
  }

  useEffect(() => {
    load()
    // Best-effort: without the lines the dialog cannot be filled in, and it says so itself.
    getRequest(trackingLinesUrl, 'shipping lines', () => {}).then(response => {
      if (response.success) setLines(response.data.results || response.data)
    })
    // eslint-disable-next-line
  }, [])

  const onDone = () => {
    setWorking(null)
    load()
  }

  /* eslint-disable react/display-name */
  const columns = [
    {
      title: 'Waiting',
      key: 'waiting',
      render: (_, r) => (
        <div>
          <div>{formatAge(r.waitingSince)}</div>
          <div style={{ opacity: 0.65 }}>{formatDateTime(r.waitingSince)}</div>
        </div>
      )
    },
    {
      title: 'Customer',
      key: 'customer',
      render: (_, r) => <ContactButtons phone={r.phone} />
    },
    {
      title: 'What we read',
      key: 'reading',
      render: (_, r) => {
        const reading = r.reading || {}
        const parts = [
          reading.lineLabel || reading.lineName,
          reading.blNumber,
          (reading.containers || []).join(', '),
          [reading.pol, reading.pod].filter(Boolean).join(' → ')
        ].filter(Boolean)
        return (
          <div>
            {parts.length ? parts.join(' · ') : <span style={{ opacity: 0.65 }}>Nothing</span>}
            {r.documentKind ? <div style={{ opacity: 0.65 }}>{r.documentKind}</div> : null}
          </div>
        )
      }
    },
    {
      title: 'Missing',
      key: 'missing',
      render: (_, r) =>
        (r.missing || []).length ? (
          <Space wrap>
            {(r.missing || []).map(name => (
              <Tag color="gold" key={name}>
                {name}
              </Tag>
            ))}
          </Space>
        ) : (
          '—'
        )
    },
    {
      title: '',
      key: 'act',
      render: (_, r) => (
        <Button type="primary" onClick={() => setWorking(r)}>
          Finish it
        </Button>
      )
    }
  ]
  /* eslint-enable react/display-name */

  return (
    <PageLoader className="container" loading={loading}>
      <PageTitle />
      <Card
        title={`Bills of lading waiting (${rows.length})`}
        extra={
          <Space>
            {loadedAt ? (
              <span style={{ opacity: 0.65 }}>{`loaded ${formatDateTime(loadedAt)}`}</span>
            ) : null}
            <Button icon={<ReloadOutlined />} onClick={load}>
              Reload
            </Button>
          </Space>
        }
      >
        <Table
          rowKey="conversationId"
          dataSource={rows}
          columns={columns}
          pagination={false}
          size="middle"
          scroll={{ x: 900 }}
          locale={{
            emptyText: (
              <Empty description="No bills of lading are waiting for a person." />
            )
          }}
        />
      </Card>

      <BillOfLadingModal
        visible={!!working}
        row={working}
        lines={lines}
        onClose={() => setWorking(null)}
        onDone={onDone}
      />
    </PageLoader>
  )
}

BillOfLadingPage.propTypes = {
  history: PropTypes.object
}

export default BillOfLadingPage
